'use client'

import React, { useMemo, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import {
  Combobox,
  ComboboxButton,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import { Skeleton } from '@/components/common/Skeleton'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'

import type { Alignment, Side } from '@/types/components/Radix'
import type { UUID } from '@/types/Id'

interface StageSelectProps {
  align?: Alignment
  side?: Side
  value: UUID
  onChange: (value: UUID) => void
  children?: React.ReactNode
  open?: boolean
  portal?: boolean
  onClose?: () => void
  // Optional className for the trigger to allow input-like styling in forms
  triggerClassName?: string
  /** Override the org for stage lookup. Defaults to active org. */
  orgId?: UUID | ''
}

export const StageSelect: React.FC<StageSelectProps> = ({
  value,
  align = 'start',
  side = 'bottom',
  onChange,
  children,
  open: openProp,
  portal = false,
  onClose,
  triggerClassName,
  orgId,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const effectiveOrgId = orgId ?? activeOrgId
  const { data: pursuitStages, isLoading } = usePursuitStages({
    orgId: effectiveOrgId,
  })
  const [open, setOpen] = useState<boolean>(openProp ?? false)

  const stages = useMemo(() => {
    if (!pursuitStages) return []
    return pursuitStages?.map((pursuitStage) => ({
      value: pursuitStage.id,
      label: pursuitStage.name,
      icon: <ColorIndicator color={pursuitStage.color} />,
    }))
  }, [pursuitStages])

  const selectedStage = useMemo(() => {
    return stages.find((stage) => stage.value === value)
  }, [stages, value])

  const handleChange = (value: UUID) => {
    onChange(value)
    setOpen(false)
    onClose?.()
  }

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    if (!open) {
      onClose?.()
    }
  }

  if (isLoading || !pursuitStages) {
    return <Skeleton className="w-24 h-6 rounded-[5px]" />
  }

  return (
    <Combobox open={open} onOpenChange={handleOpenChange}>
      <ComboboxTrigger asChild={children ? false : true}>
        {children ? (
          children
        ) : (
          <ComboboxButton className={twMerge('px-[6px]', triggerClassName)}>
            {selectedStage ? (
              <>
                {selectedStage.icon}
                <span>{selectedStage.label}</span>
              </>
            ) : (
              <span className="pl-[2px] text-primary-500 dark:text-primary-400">
                + Select stage
              </span>
            )}
          </ComboboxButton>
        )}
      </ComboboxTrigger>
      <ComboboxContent
        className="p-0 sm:max-w-48"
        side={side}
        align={align}
        portal={portal}
      >
        <ComboboxInput
          placeholder="Change stage..."
          className="placeholder:text-[13px]"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-[13px]">
            No results found.
          </ComboboxEmpty>
          <ComboboxGroup>
            {stages.map((stage) => {
              const isSelected = stage.value === selectedStage?.value
              return (
                <ComboboxSelectableItem
                  key={stage.value}
                  // for searching
                  value={stage.label}
                  onSelect={() => handleChange(stage.value)}
                  isSelected={isSelected}
                >
                  <div className="flex items-center gap-x-2">
                    {stage.icon}
                    <span>{stage.label}</span>
                  </div>
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

StageSelect.displayName = 'PursuitForm.StageSelect'
